/**
 * «Μεγέθη & τιμές»: εδώ ζει ΟΛΗ η τιμή του προϊόντος (Φ5). Τιμή + Take Away / Dine-In / Delivery,
 * κενό κανάλι = η «Τιμή» — καμία στήλη efood/Wolt (απόφαση ιδιοκτήτη 12/10/2026), μόνο η σημείωση.
 * Η απόκρυψη αποθηκευμένου μεγέθους από το ταμείο είναι άμεση (όπως ως σήμερα, και σε κεντρικό
 * κατάλογο)· όλα τα άλλα περνούν από το πρόχειρο.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Eye, EyeOff, GripVertical, Info, Layers, MoreHorizontal, Plus, Star, Trash2 } from 'lucide-react';
import { formatEuro } from '../../utils/formatEuro';
import { isFeeProduct } from '../../utils/pricing';
import { ApiSizeGroup, FormSize, newSize, sizesFromGroup, withDefaultSize } from './catalogModel';
import type { ProductState } from './catalogDraft';
import { cx, MenuButton, MoneyInput, Switch } from './ui';
import { DragState, dropHalf, moveItem } from './dragState';

export interface SizesTabProps {
  productKey: string;
  state: ProductState;
  changed: Set<string>;
  readOnly: boolean;
  feeGross: number | null;
  sizeGroups: ApiSizeGroup[];
  drag: DragState;
  onChange: (fn: (s: ProductState) => ProductState) => void;
  /** άμεσο: απόκρυψη/εμφάνιση αποθηκευμένου μεγέθους στο ταμείο */
  onSizeVisibility: (size: FormSize) => void;
  onNewSizeGroup: (name: string, items: Array<{ name: string; price_modifier: number; is_default: boolean }>) => void;
}

type Channel = 'price_modifier_take_away' | 'price_modifier_dine_in' | 'price_modifier_delivery';
const CHANNELS: Array<{ key: Channel; label: string }> = [
  { key: 'price_modifier_take_away', label: 'catalog:sizes.takeAway' },
  { key: 'price_modifier_dine_in', label: 'catalog:sizes.dineIn' },
  { key: 'price_modifier_delivery', label: 'catalog:sizes.delivery' },
];

const hasChannelPrices = (sizes: FormSize[]) => sizes.some(s => CHANNELS.some(c => s[c.key] !== null && s[c.key] !== undefined));

const ProductSizesTab: React.FC<SizesTabProps> = props => {
  const { t } = useTranslation(['catalog']);
  const { state, changed, readOnly, drag, productKey } = props;
  const f = state.form;
  const sizes = f.sizes;
  const listId = `sizes:${productKey}`;
  const [perChannelPref, setPerChannelPref] = useState<boolean | null>(null);
  const [over, setOver] = useState<{ uid: string; pos: 'before' | 'after' } | null>(null);
  const [newGroupName, setNewGroupName] = useState<string | null>(null);
  const channelValues = hasChannelPrices(sizes);
  // Με τιμές καναλιού οι στήλες μένουν ανοιχτές: δεν κρύβουμε ποσά που ισχύουν.
  const perChannel = channelValues || (perChannelPref ?? false);
  const isFee = isFeeProduct(f);

  const setSizes = (fn: (list: FormSize[]) => FormSize[]) => props.onChange(s => ({ ...s, form: { ...s.form, sizes: fn(s.form.sizes) } }));
  const update = (uid: string, patch: Partial<FormSize>) => setSizes(list => list.map(s => (s._uid === uid ? { ...s, ...patch } : s)));
  const mark = (uid: string, part: string) => (changed.has(`size:${uid}:${part}`) || changed.has(`size:${uid}`) ? 'cx-changed' : undefined);
  const hiddenNames = sizes.filter(s => s.is_hidden && s.id).map(s => s.name);

  if (isFee && sizes.length === 0) {
    return (
      <div className="cx-note">
        <Info className="cx-ico" />
        <span>{props.feeGross !== null ? t('catalog:sizes.feeNoSizesAmount', { amount: formatEuro(props.feeGross) }) : t('catalog:sizes.feeNoSizes')}</span>
      </div>
    );
  }

  const onDrop = (e: React.DragEvent, uid: string) => {
    e.preventDefault();
    const d = drag.current;
    const pos = over && over.uid === uid ? over.pos : dropHalf(e);
    setOver(null);
    drag.end();
    if (!d || d.kind !== 'row' || d.list !== listId || d.uid === uid) return;
    setSizes(list => {
      const from = list.findIndex(s => s._uid === d.uid);
      let to = list.findIndex(s => s._uid === uid);
      if (from < 0 || to < 0) return list;
      if (pos === 'after' && to < from) to += 1;
      if (pos === 'before' && to > from) to -= 1;
      return moveItem(list, from, to);
    });
  };

  return (
    <>
      <div className="cx-sec">
        <div className="cx-sec-top">
          <div>
            <div className="cx-sec-title">{t('catalog:sizes.title')}</div>
            <p className="cx-hint">{t('catalog:sizes.hint')}</p>
          </div>
          <label className="cx-sw-label" title={channelValues ? t('catalog:sizes.perChannelLocked') : undefined}>
            <Switch checked={perChannel} disabled={channelValues || readOnly} label={t('catalog:sizes.perChannel')}
              onChange={next => setPerChannelPref(next)} />
            {t('catalog:sizes.perChannel')}
          </label>
        </div>
        {hiddenNames.length > 0 && (
          <p className="cx-hint warn" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <EyeOff className="cx-ico" style={{ width: 14, height: 14 }} />
            {t('catalog:sizes.hiddenNote', { names: hiddenNames.join(', ') })}
          </p>
        )}
        <div className={cx('cx-sz', perChannel && 'perch')} role="table" aria-label={t('catalog:sizes.title')}>
          <div className="cx-sz-h" role="row">
            <span />
            <span title={t('catalog:sizes.defaultTitle')}><Star className="cx-ico" /></span>
            <span>{t('catalog:sizes.name')}</span>
            <span className="r">{t('catalog:sizes.price')}</span>
            {CHANNELS.map(c => <span key={c.key} className="r cx-ch">{t(c.label)}</span>)}
            <span />
          </div>
          {sizes.map((s, index) => {
            const drop = over && over.uid === s._uid ? over.pos : null;
            const named = s.name.trim() || t('catalog:sizes.unnamed');
            const removeLocked = !isFee && sizes.length <= 1;
            return (
              <div
                key={s._uid}
                role="row"
                data-testid={`size-row-${s._uid}`}
                className={cx('cx-sz-r', drop && `drop-${drop}`, s.is_hidden && 'hidden-size')}
                onDragOver={e => { if (drag.current?.kind === 'row' && drag.current.list === listId) { e.preventDefault(); setOver({ uid: s._uid, pos: dropHalf(e) }); } }}
                onDragLeave={() => setOver(o => (o && o.uid === s._uid ? null : o))}
                onDrop={e => onDrop(e, s._uid)}
              >
                {readOnly ? <span /> : (
                  <button
                    type="button"
                    className="cx-grip"
                    draggable
                    aria-label={t('catalog:sizes.reorder', { name: named })}
                    title={t('catalog:list.reorderHint')}
                    onDragStart={e => { drag.start({ kind: 'row', list: listId, uid: s._uid }); try { e.dataTransfer.setData('text/plain', s._uid); } catch { /* jsdom */ } }}
                    onDragEnd={() => { drag.end(); setOver(null); }}
                    onKeyDown={e => {
                      if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
                      e.preventDefault();
                      setSizes(list => moveItem(list, index, index + (e.key === 'ArrowUp' ? -1 : 1)));
                    }}
                  >
                    <GripVertical className="cx-ico" />
                  </button>
                )}
                <input
                  type="radio"
                  name={`size-default-${productKey}`}
                  checked={s.is_default}
                  disabled={readOnly}
                  aria-label={t('catalog:sizes.defaultFor', { name: named })}
                  title={t('catalog:sizes.defaultTitle')}
                  onChange={() => setSizes(list => withDefaultSize(list, s._uid))}
                />
                <input
                  className={cx('cx-inp sm', mark(s._uid, 'name'))}
                  value={s.name}
                  disabled={readOnly}
                  placeholder={t('catalog:sizes.namePlaceholder')}
                  aria-label={t('catalog:sizes.nameOf', { index: index + 1 })}
                  onChange={e => update(s._uid, { name: e.target.value })}
                />
                <MoneyInput
                  value={s.price_modifier}
                  label={t('catalog:sizes.priceOf', { name: named, channel: t('catalog:sizes.price') })}
                  placeholder="0,00"
                  disabled={readOnly}
                  changed={!!mark(s._uid, 'price_modifier')}
                  onChange={v => update(s._uid, { price_modifier: v ?? 0 })}
                />
                {CHANNELS.map(c => (
                  <MoneyInput
                    key={c.key}
                    className="cx-ch"
                    value={s[c.key]}
                    nullable
                    disabled={readOnly}
                    label={t('catalog:sizes.priceOf', { name: named, channel: t(c.label) })}
                    placeholder={s.price_modifier.toFixed(2).replace('.', ',')}
                    title={t('catalog:sizes.channelEmptyHint')}
                    changed={!!mark(s._uid, c.key)}
                    onChange={v => update(s._uid, { [c.key]: v } as Partial<FormSize>)}
                  />
                ))}
                <MenuButton
                  label={t('catalog:sizes.actions', { name: named })}
                  className="cx-icon-btn sm"
                  align="right"
                  items={[
                    {
                      key: 'vis',
                      label: s.is_hidden ? t('catalog:sizes.show') : t('catalog:sizes.hide'),
                      icon: s.is_hidden ? <Eye className="cx-ico" /> : <EyeOff className="cx-ico" />,
                      // Αποθηκευμένο μέγεθος: άμεσο (επιχειρησιακό, και σε κεντρικό κατάλογο)· νέο: στο πρόχειρο.
                      onSelect: () => (s.id ? props.onSizeVisibility(s) : update(s._uid, { is_hidden: !s.is_hidden })),
                      disabled: !s.id && readOnly,
                    },
                    {
                      key: 'del',
                      label: t('catalog:sizes.remove'),
                      icon: <Trash2 className="cx-ico" />,
                      danger: true,
                      disabled: readOnly || removeLocked,
                      hint: removeLocked ? t('catalog:sizes.lastSize') : undefined,
                      onSelect: () => setSizes(list => list.filter(x => x._uid !== s._uid)),
                    },
                  ]}
                >
                  <MoreHorizontal className="cx-ico" />
                </MenuButton>
              </div>
            );
          })}
        </div>
        {!readOnly && (
          <div className="cx-btn-row">
            <button type="button" className="cx-btn ghost sm" onClick={() => setSizes(list => [...list, newSize('', list.length === 0)])}>
              <Plus className="cx-ico" /> {t('catalog:sizes.add')}
            </button>
          </div>
        )}
      </div>

      {!readOnly && (
        <div className="cx-box">
          <div className="cx-sec-title sm">{t('catalog:sizes.fromGroupTitle')}</div>
          <p className="cx-hint">{t('catalog:sizes.fromGroupHint')}</p>
          {newGroupName === null ? (
            <div className="cx-btn-row">
              <MenuButton
                label={t('catalog:sizes.copyFromGroup')}
                className="cx-btn sm"
                disabled={props.sizeGroups.length === 0}
                title={props.sizeGroups.length === 0 ? t('catalog:sizes.noGroups') : t('catalog:sizes.copyFromGroup')}
                items={props.sizeGroups.map(g => ({
                  key: String(g.id),
                  label: g.name,
                  hint: t('catalog:sizes.groupCount', { count: (g.items || []).length }),
                  onSelect: () => setSizes(() => sizesFromGroup(g)),
                }))}
              >
                <Layers className="cx-ico" /> {t('catalog:sizes.copyFromGroup')} <ChevronDown className="cx-ico" />
              </MenuButton>
              <button type="button" className="cx-btn ghost sm" disabled={sizes.length === 0} onClick={() => setNewGroupName('')}>
                <Plus className="cx-ico" /> {t('catalog:sizes.newGroupFromThese')}
              </button>
            </div>
          ) : (
            <div className="cx-btn-row">
              <input className="cx-inp sm" style={{ flex: 1, minWidth: 140 }} autoFocus value={newGroupName}
                placeholder={t('catalog:sizes.newGroupName')} aria-label={t('catalog:sizes.newGroupName')} onChange={e => setNewGroupName(e.target.value)} />
              <button type="button" className="cx-btn ghost sm" onClick={() => setNewGroupName(null)}>{t('catalog:common.cancel')}</button>
              <button
                type="button"
                className="cx-btn primary sm"
                disabled={!newGroupName.trim()}
                onClick={() => {
                  props.onNewSizeGroup(newGroupName.trim(), sizes.filter(s => s.name.trim()).map(s => ({ name: s.name.trim(), price_modifier: s.price_modifier, is_default: s.is_default })));
                  setNewGroupName(null);
                }}
              >
                {t('catalog:common.add')}
              </button>
              <p className="cx-hint" style={{ flexBasis: '100%' }}>{t('catalog:sizes.newGroupSavedWith')}</p>
            </div>
          )}
        </div>
      )}

      <div className="cx-note">
        <Info className="cx-ico" />
        <span>{t('catalog:sizes.efoodNote')}</span>
      </div>
    </>
  );
};

export default ProductSizesTab;
